import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authAPI, quotaAPI, projectsAPI } from "@/lib/api";

const AppContext = createContext(null);

export function AppProvider({ children }) {
    const [lang, setLang] = useState(() => localStorage.getItem("sf_lang") || "fr");
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quota, setQuota] = useState(null);
    const [projects, setProjects] = useState([]);
    const [projectsSummary, setProjectsSummary] = useState({ capacity: 0, allocated: 0, unallocated: 0 });
    const [activeProjectId, setActiveProjectId] = useState(() => localStorage.getItem("sf_active_project") || null);

    useEffect(() => {
        localStorage.setItem("sf_lang", lang);
    }, [lang]);

    useEffect(() => {
        if (activeProjectId) localStorage.setItem("sf_active_project", activeProjectId);
    }, [activeProjectId]);

    const refresh = useCallback(async () => {
        // If returning from Emergent OAuth callback, skip /me — AuthCallback will handle it.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        const token = localStorage.getItem("sf_token");
        if (!token) { setUser(null); setLoading(false); return; }
        try {
            const { user } = await authAPI.me();
            setUser(user);
        } catch {
            localStorage.removeItem("sf_token");
            setUser(null);
        } finally { setLoading(false); }
    }, []);

    const refreshQuota = useCallback(async () => {
        const token = localStorage.getItem("sf_token");
        if (!token) { setQuota(null); return null; }
        try {
            const q = await quotaAPI.me();
            setQuota(q);
            return q;
        } catch {
            setQuota(null);
            return null;
        }
    }, []);

    const refreshProjects = useCallback(async () => {
        const token = localStorage.getItem("sf_token");
        if (!token) { setProjects([]); setProjectsSummary({ capacity: 0, allocated: 0, unallocated: 0 }); return null; }
        try {
            const d = await projectsAPI.list();
            setProjects(d.projects || []);
            setProjectsSummary({ capacity: d.capacity, allocated: d.allocated, unallocated: d.unallocated });
            // Auto-select first real project (else demo)
            if (!activeProjectId && d.projects?.length) {
                const first = d.projects.find((p) => !p.is_demo) || d.projects[0];
                setActiveProjectId(first.id);
            }
            return d;
        } catch { return null; }
    }, [activeProjectId]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => { if (user) { refreshQuota(); refreshProjects(); } else { setQuota(null); setProjects([]); } }, [user, refreshQuota, refreshProjects]);

    const login = async (email, password) => {
        const res = await authAPI.login({ email, password });
        localStorage.setItem("sf_token", res.token);
        setUser(res.user);
        return res.user;
    };
    const register = async (email, password, name) => {
        const referral_code = localStorage.getItem("sf_ref") || undefined;
        const res = await authAPI.register({ email, password, name, referral_code });
        localStorage.setItem("sf_token", res.token);
        setUser(res.user);
        return res.user;
    };
    const logout = () => {
        localStorage.removeItem("sf_token");
        setUser(null);
        setQuota(null);
    };

    return (
        <AppContext.Provider value={{ lang, setLang, user, loading, login, register, logout, refresh, quota, refreshQuota, projects, projectsSummary, refreshProjects, activeProjectId, setActiveProjectId }}>
            {children}
        </AppContext.Provider>
    );
}

export const useApp = () => useContext(AppContext);
